import { useRef, useState } from "react";
import { CheckCircle2, Upload, XCircle } from "lucide-react";
import { getRealizadoImportacoes, importarRealizadoCsv } from "../../api";
import RealizadoLayout from "../../components/gestaoRealizado/RealizadoLayout";
import {
  AvisoErro, Card, Carregando, botaoPequeno, estiloTabela, formatBRL, formatCompetencia, formatData, useCarregar, useTema,
} from "../../components/gestaoRealizado/comum";

// Carga HISTÓRICA (CSV exportado da planilha "Solicitação emissão de NF"). Daqui para frente os
// dados vêm do formulário da plataforma e do OMIE (botão em Notas fiscais). Idempotente: pode
// importar o mesmo arquivo de novo — atualiza o que mudou, não duplica. Ver
// backend/services/realizado_faturamento_carga.py.

const FONTES = [
  {
    id: "RESPOSTAS",
    titulo: "Aba Respostas",
    descricao: "Solicitações de NF (uma linha por envio do formulário). Linhas a partir da 1ª solicitação gravada pela plataforma são ignoradas — essas já estão no banco.",
  },
  {
    id: "EMISSAO_COMP",
    titulo: "Aba Emissão de Nota (Competência)",
    descricao: "Uma linha por competência de cada solicitação (desde 11/09/2026), com “É competência?” e Nº da Nota. Importe depois da aba Respostas: detalha essas solicitações e baixa o saldo das competências.",
  },
  {
    id: "CONTROLE_COMP",
    titulo: "Aba Controle comp.",
    descricao: "Competências controladas e as NFs que baixam o saldo (até 3 por linha). O saldo é recalculado e comparado com a coluna Saldo Bruto.",
  },
  {
    id: "NFE_OFICIAL",
    titulo: "Aba Nfe oficial",
    descricao: "Dados das notas (mesma linha da aba Respostas): nº, emissão, retenções, líquido, recebimento e competência da DRE. Importe por último.",
  },
];

function Conferencia({ C, fonte, r }) {
  const T = estiloTabela(C);
  const item = (rotulo, valor, ok) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
      <span style={{ color: C.muted }}>{rotulo}</span>
      <span style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 4, fontVariantNumeric: "tabular-nums" }}>
        {valor}
        {ok === true && <CheckCircle2 size={13} color={C.success} />}
        {ok === false && <XCircle size={13} color={C.danger} />}
      </span>
    </div>
  );
  const erros = Object.entries(r.erros || {});
  return (
    <div style={{ marginTop: 12 }}>
      {fonte === "NFE_OFICIAL" ? (
        <>
          {item("Linhas lidas", r.linhasLidas)}
          {item("Linhas com NF", r.linhasComNf)}
          {item("Notas distintas", r.nfsDistintas)}
          {item("Notas em mais de uma linha", r.nfsCompartilhadas)}
          {item("Linhas sem nº de NF", r.semNumeroNf)}
          {item("Linhas desalinhadas com a aba Respostas", r.desalinhadas, r.desalinhadas === 0)}
          {item("Notas canceladas", r.canceladas)}
        </>
      ) : fonte === "EMISSAO_COMP" ? (
        <>
          {item("Linhas lidas", r.linhasLidas)}
          {item("Competências importadas", r.importadas)}
          {item("Solicitações detalhadas (casadas com a aba Respostas)", r.solicitacoesDetalhadas)}
          {item("Solicitações criadas (não estavam na aba Respostas)", r.solicitacoesCriadas)}
          {item("Marcadas como competência", r.marcadasComoCompetencia)}
          {item("Baixas de saldo geradas", r.baixasPorSolicitacao)}
          {item("Já lançadas no Valor Faturado (mesma NF)", r.solicitacoesJaFaturadas)}
        </>
      ) : fonte === "RESPOSTAS" ? (
        <>
          {item("Linhas lidas", r.linhasLidas)}
          {item("Importadas", r.importadas)}
          {item("Descartadas (teste / data / competência inválida)", r.descartadas)}
          {item("Ignoradas (já gravadas pela plataforma)", r.ignoradasPosCorte)}
          {item("Σ planilha (sem TESTE)", formatBRL(r.somaPlanilhaSemTeste))}
          {item("Σ importado + ignorado", formatBRL(r.somaImportada + r.somaIgnoradaPosCorte), Math.abs(r.diferencaNaoExplicada) < 0.01)}
        </>
      ) : (
        <>
          {item("Linhas lidas (com conteúdo)", r.linhasLidas)}
          {item("Competências importadas", r.importadas)}
          {item("Linhas sem projeto (ignoradas)", r.ignoradasSemProjeto)}
          {item("Projetos distintos", r.projetosDistintos)}
          {item("Saldo aberto na planilha", `${r.saldoAbertoPlanilha.competencias} · ${formatBRL(r.saldoAbertoPlanilha.valor)}`,
            r.saldoAbertoPlanilha.competencias === r.referenciaFinanceiro.competencias && Math.abs(r.saldoAbertoPlanilha.valor - r.referenciaFinanceiro.valor) < 0.01)}
          {item("Saldo aberto calculado (banco)", `${r.saldoAbertoBanco.competencias} · ${formatBRL(r.saldoAbertoBanco.valor)}`,
            (r.divergenciasSaldo || []).length === 0)}
          {item("Baixas por solicitação “É competência? = Sim”", r.baixasPorSolicitacao)}
          {(r.divergenciasSaldo || []).length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.danger, marginBottom: 6 }}>
                {r.divergenciasSaldo.length} linha(s) com Saldo Bruto da planilha diferente do saldo calculado:
              </div>
              <div style={T.wrap}>
                <table style={T.table}>
                  <thead><tr>
                    <th style={T.th}>Linha</th><th style={T.th}>Projeto</th><th style={T.th}>Comp.</th>
                    <th style={{ ...T.th, ...T.thNum }}>Calculado</th><th style={{ ...T.th, ...T.thNum }}>Planilha</th>
                  </tr></thead>
                  <tbody>
                    {r.divergenciasSaldo.map((d) => (
                      <tr key={d.linha}>
                        <td style={T.td}>{d.linha}</td><td style={T.td}>{d.projeto}</td><td style={T.td}>{formatCompetencia(d.competencia)}</td>
                        <td style={{ ...T.td, ...T.tdNum }}>{formatBRL(d.saldoCalculado)}</td><td style={{ ...T.td, ...T.tdNum }}>{formatBRL(d.saldoPlanilha)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
      {erros.length > 0 && (
        <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>
          Ocorrências registradas no log: {erros.map(([t, n]) => `${t} (${n})`).join(" · ")}
        </div>
      )}
    </div>
  );
}

function CartaoImportacao({ C, fonte, onImportado }) {
  const inputRef = useRef(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState("");

  const importar = async (arquivo) => {
    if (!arquivo) return;
    setEnviando(true);
    setErro("");
    setResultado(null);
    try {
      setResultado(await importarRealizadoCsv(fonte.id, arquivo));
      onImportado();
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Card
      C={C}
      titulo={fonte.titulo}
      subtitulo={fonte.descricao}
      acoes={
        <button style={{ ...botaoPequeno(C.brand), opacity: enviando ? 0.6 : 1 }} disabled={enviando} onClick={() => inputRef.current?.click()}>
          <Upload size={13} /> {enviando ? "Importando…" : "Selecionar CSV"}
        </button>
      }
    >
      <input ref={inputRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={(e) => importar(e.target.files?.[0])} />
      <AvisoErro C={C}>{erro}</AvisoErro>
      {resultado ? <Conferencia C={C} fonte={fonte.id} r={resultado} /> : (
        <div style={{ fontSize: 12, color: C.muted }}>Exporte a aba como CSV (Arquivo → Fazer download → .csv) e selecione o arquivo.</div>
      )}
    </Card>
  );
}

export default function ImportacaoRealizado() {
  const [isLight, setIsLight, C] = useTema();
  const { dados, carregando, erro, recarregar } = useCarregar(() => getRealizadoImportacoes(), []);
  const T = estiloTabela(C);

  return (
    <RealizadoLayout titulo="Importação (CSV)" isLight={isLight} setIsLight={setIsLight}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 16, alignItems: "start" }}>
        {FONTES.map((f) => <CartaoImportacao key={f.id} C={C} fonte={f} onImportado={recarregar} />)}
      </div>
      <AvisoErro C={C}>{erro}</AvisoErro>
      <Card C={C} titulo="Histórico de importações">
        {carregando && !dados ? <Carregando C={C} /> : (
          <div style={T.wrap}>
            <table style={T.table}>
              <thead><tr>
                <th style={T.th}>#</th><th style={T.th}>Data</th><th style={T.th}>Aba</th><th style={T.th}>Arquivo</th><th style={T.th}>Usuário</th>
                <th style={{ ...T.th, ...T.thNum }}>Lidas</th><th style={{ ...T.th, ...T.thNum }}>OK</th>
                <th style={{ ...T.th, ...T.thNum }}>Descartadas</th><th style={{ ...T.th, ...T.thNum }}>Ignoradas</th>
              </tr></thead>
              <tbody>
                {(dados || []).map((i) => (
                  <tr key={i.id}>
                    <td style={T.td}>{i.id}</td>
                    <td style={{ ...T.td, whiteSpace: "nowrap" }}>{formatData(i.iniciadoEm)} {i.iniciadoEm.slice(11, 16)}</td>
                    <td style={T.td}>{{ RESPOSTAS: "Respostas", EMISSAO_COMP: "Emissão de Nota (Comp.)", CONTROLE_COMP: "Controle comp.", NFE_OFICIAL: "Nfe oficial" }[i.fonte] || i.fonte}</td>
                    <td style={T.td}>{i.arquivo}</td>
                    <td style={T.td}>{i.usuario}</td>
                    <td style={{ ...T.td, ...T.tdNum }}>{i.linhasLidas}</td>
                    <td style={{ ...T.td, ...T.tdNum }}>{i.linhasOk}</td>
                    <td style={{ ...T.td, ...T.tdNum }}>{i.linhasErro}</td>
                    <td style={{ ...T.td, ...T.tdNum }}>{i.linhasIgnoradas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </RealizadoLayout>
  );
}
